import React, { useState, useEffect, useRef } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { auth } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import Timkiem from "./Timkiem";

// 🌸 IMPORT FILE LOGO TỪ THƯ MỤC ASSETS
import logoImg from "./assets/LOGO.png";

// --- BỘ ICON SVG CHUẨN UI/UX THIẾT KẾ MỚI SANG TRỌNG ---
const Icons = {
  User: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Login: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="16" y1="11" x2="22" y2="11" />
    </svg>
  ),
  Menu: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  ),
  Close: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Cart: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
  ),
  ChevronDown: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: "4px", verticalAlign: "middle" }}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  )
};

// Đưa các mảng dữ liệu tĩnh ra ngoài Component để tránh khởi tạo lại khi re-render
const hoaTheoDipItems = [
  { name: "Hoa kỉ niệm", path: "/dung-dip/ki-niem" },
  { name: "Hoa sinh nhật", path: "/dung-dip/sinh-nhat" },
  { name: "Hoa khai trương", path: "/dung-dip/khai-truong" },
  { name: "Hoa chia buồn", path: "/dung-dip/chia-buon" },
  { name: "Hoa tốt nghiệp", path: "/dung-dip/tot-nghiep" }
];

const hoaHopTuiItems = [
  { name: "Dưới 250.000 VNĐ", path: "/hop-tui/duoi-250k" },
  { name: "Từ 250.000 VNĐ đến 500.000 VNĐ", path: "/hop-tui/250k-500k" },
  { name: "Từ 500.000 VNĐ đến 1.000.000 VNĐ", path: "/hop-tui/500k-1000k" },
  { name: "Từ 1.000.000 VNĐ đến 2.000.000 VNĐ", path: "/hop-tui/1000k-2000k" },
  { name: "Trên 2.000.000 VNĐ", path: "/hop-tui/tren-2000k" }
];

export default function MainLayout() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1150);

  // --- STATES QUẢN LÝ HOVER DROPDOWN MENU ---
  const [hoveredMenu, setHoveredMenu] = useState(null);

  // --- STATES QUẢN LÝ TÀI KHOẢN ---
  const [currentUser, setCurrentUser] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // --- STATES QUẢN LÝ GIỎ HÀNG ---
  const [cartCount, setCartCount] = useState(0);

  // Theo dõi trạng thái đăng nhập từ Firebase & Thiết lập Resize Window chung một useEffect
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null);
    });

    const handleResize = () => setIsMobile(window.innerWidth < 1150);
    window.addEventListener("resize", handleResize);

    // Đếm số lượng items trong giỏ hàng
    const updateCartCount = () => {
      const cart = JSON.parse(localStorage.getItem("sellixy_cart")) || [];
      const totalItems = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
      setCartCount(totalItems);
    };

    updateCartCount();

    // Lắng nghe custom event khi giỏ hàng thay đổi
    window.addEventListener("cartUpdated", updateCartCount);
    // Lắng nghe storage event khi thay đổi từ tab khác
    window.addEventListener("storage", updateCartCount);

    return () => {
      unsubscribeAuth();
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("cartUpdated", updateCartCount);
      window.removeEventListener("storage", updateCartCount);
    };
  }, []);

  // Đóng dropdown tài khoản khi click ra ngoài vùng kiểm soát
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Xử lý hành động Đăng xuất
  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("flower_user");
      setShowDropdown(false);
      alert("Đã đăng xuất tài khoản! 🌸");
    } catch (error) {
      console.error("Lỗi đăng xuất:", error);
    }
  };

  const go = (path) => {
    navigate(path);
    setOpen(false);
    setHoveredMenu(null);
  };

  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif", background: "#fff", overflowX: "hidden" }}>
      {/* 🌸 NHÚNG CSS HIỆU ỨNG GLOBAL - ĐÃ CẬP NHẬT FONT CHỮ SANG TRỌNG */}
      <style>{`
        .nav-link-container {
          position: relative;
          padding: 20px 0;
          display: inline-flex;
          align-items: center;
        }
        .nav-link-item {
          position: relative;
          cursor: pointer;
          transition: all 0.2s ease;
          color: #1a1a1a;
          font-family: 'Montserrat', sans-serif;
          font-weight: 500;
          font-size: 13.5px;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          white-space: nowrap;
          display: flex;
          align-items: center;
        }
        .nav-link-item:hover {
          color: #000000;
          opacity: 0.8;
        }
        .nav-link-item::after {
          content: '';
          position: absolute;
          width: 100%;
          transform: scaleX(0);
          height: 1.5px;
          bottom: -4px;
          left: 0;
          background-color: #1a1a1a;
          transform-origin: bottom right;
          transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .nav-link-item:hover::after {
          transform: scaleX(1);
          transform-origin: bottom left;
        }
        .dropdown-row-item:hover {
          background-color: #f8f8fa;
          color: #ff66a2 !important;
        }
        .mega-menu-item {
          font-family: 'Montserrat', sans-serif;
          padding: 10px 16px;
          font-size: 13px;
          color: #2c2c2c;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
          border-radius: 8px;
          text-align: left;
        }
        .mega-menu-item:hover {
          background: #fff0f5;
          color: #ff66a2;
          padding-left: 20px;
        }
      `}</style>

      {/* 🌸 HEADER / NAVBAR CHÍNH */}
      <header style={header}>
        {/* 🖼️ LOGO DẠNG HÌNH ẢNH NẰM NGOÀI CÙNG BÊN TRÁI */}
        <div style={logoContainer}>
          <motion.img
            src={logoImg}
            alt="Floraison Logo"
            style={logoStyle}
            onClick={() => go("/")}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          />
        </div>

        {/* Menu Điều Hướng Ở Giữa */}
        {!isMobile && (
          <div style={navCenterGroup}>
            <div className="nav-link-container"><span className="nav-link-item" onClick={() => go("/")}>Trang chủ</span></div>
            <div className="nav-link-container"><span className="nav-link-item" onClick={() => go("/ve-chung-toi")}>Về chúng tôi</span></div>
            <div className="nav-link-container"><span className="nav-link-item" onClick={() => go("/thiet-ke-bo-hoa")}>Thiết kế hoa</span></div>
            
            {/* Mục: Hoa theo dịp */}
            <div 
              className="nav-link-container"
              onMouseEnter={() => setHoveredMenu("dip")}
              onMouseLeave={() => setHoveredMenu(null)}
            >
              <span className="nav-link-item" onClick={() => go("/dung-dip")}>
                Hoa đúng dịp <Icons.ChevronDown />
              </span>
              <AnimatePresence>
                {hoveredMenu === "dip" && (
                  <motion.div
                    style={megaDropdown}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 15 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    {hoaTheoDipItems.map((item, index) => (
                      <div key={index} className="mega-menu-item" onClick={() => go(item.path)}>
                        {item.name}
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mục: Hoa hợp túi */}
            <div 
              className="nav-link-container"
              onMouseEnter={() => setHoveredMenu("tui")}
              onMouseLeave={() => setHoveredMenu(null)}
            >
              <span className="nav-link-item" onClick={() => go("/hop-tui")}>
                Hoa hợp túi <Icons.ChevronDown />
              </span>
              <AnimatePresence>
                {hoveredMenu === "tui" && (
                  <motion.div
                    style={{ ...megaDropdown, minWidth: "260px" }}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 15 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    {hoaHopTuiItems.map((item, index) => (
                      <div key={index} className="mega-menu-item" onClick={() => go(item.path)}>
                        {item.name}
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div 
              className="nav-link-container"
              onMouseEnter={() => setHoveredMenu("matngu")}
              onMouseLeave={() => setHoveredMenu(null)}
            >
              <span className="nav-link-item">
                Mật ngữ loài hoa <Icons.ChevronDown />
              </span>
              <AnimatePresence>
                {hoveredMenu === "matngu" && (
                  <motion.div
                    style={megaDropdown}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 15 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <div className="mega-menu-item" onClick={() => go("/mat-ngu-loai-hoa/theo-mau")}>
                      Theo màu
                    </div>
                    <div className="mega-menu-item" onClick={() => go("/mat-ngu-loai-hoa/theo-y-nghia")}>
                      Theo ý nghĩa
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="nav-link-container"><span className="nav-link-item" onClick={() => go("/lien-he")}>Liên hệ</span></div>
          </div>
        )}

        {/* Cụm Tiện Ích Phải */}
        <div style={navRightGroup}>
          
          {/* 🔍 KHU VỰC TÌM KIẾM TÍCH HỢP BẰNG COMPONENT TIMKIEM */}
          <div style={{ display: "flex", alignItems: "center" }}>
            <Timkiem />
          </div>

          {/* 🛒 ICON GIỎ HÀNG */}
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <motion.button
              type="button"
              style={actionIconBtn}
              onClick={() => go("/gio-hang-cua-ban")}
              whileHover={{ scale: 1.08, backgroundColor: "rgba(0, 0, 0, 0.06)" }}
              whileTap={{ scale: 0.92 }}
            >
              <Icons.Cart />
            </motion.button>
            {cartCount > 0 && (
              <div style={{
                position: "absolute", top: "-8px", right: "-8px", backgroundColor: "#e53935",
                color: "#ffffff", width: "22px", height: "22px", borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "11px", fontWeight: "700", fontFamily: '"Montserrat", sans-serif'
              }}>
                {cartCount}
              </div>
            )}
          </div>

          {/* 👤 CỤM TÀI KHOẢN & ĐĂNG NHẬP */}
          <div style={{ position: "relative", display: "flex", alignItems: "center" }} ref={dropdownRef}>
            <motion.button
              type="button"
              style={{ ...actionIconBtn, backgroundColor: currentUser ? "rgba(0, 0, 0, 0.05)" : "transparent" }}
              onClick={() => currentUser ? setShowDropdown(!showDropdown) : go("/dang-nhap")}
              whileHover={{ scale: 1.08, backgroundColor: currentUser ? "rgba(0, 0, 0, 0.1)" : "rgba(0, 0, 0, 0.06)" }}
              whileTap={{ scale: 0.92 }}
            >
              {currentUser ? <Icons.User /> : <Icons.Login />}
            </motion.button>

            {/* Dropdown Menu Account */}
            <AnimatePresence>
              {showDropdown && currentUser && (
                <motion.div
                  style={dropdownMenu}
                  initial={{ opacity: 0, y: 12, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 12, scale: 0.95 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                >
                  <div style={dropdownHeader}>
                    <p style={dropdownPhone}>Thành viên</p>
                    <p style={dropdownEmail}>{currentUser.email || currentUser.phoneNumber}</p>
                  </div>
                  <hr style={{ border: "0", height: "1px", background: "#f0f0f5", margin: "4px 0" }} />
                  <div className="dropdown-row-item" style={dropdownItem} onClick={() => { go("/theo-doi-don-hang"); setShowDropdown(false); }}>
                    Theo dõi đơn hàng
                  </div>
                  <div className="dropdown-row-item" style={{ ...dropdownItem, color: "#e63946" }} onClick={handleLogout}>
                    Đăng xuất tài khoản
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Icon Menu Mở Mobile Sidebar */}
          {isMobile && (
            <motion.button
              type="button"
              style={actionIconBtn}
              onClick={() => setOpen(true)}
              whileHover={{ scale: 1.08, backgroundColor: "rgba(0, 0, 0, 0.06)" }}
              whileTap={{ scale: 0.92 }}
            >
              <Icons.Menu />
            </motion.button>
          )}
        </div>
      </header>

      {/* 🌸 SIDEBAR DI ĐỘNG (MOBILE) */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div style={overlay} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} />
            <motion.div style={sideMenu} initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", duration: 0.25 }}>
              <div style={closeBtn} onClick={() => setOpen(false)}>
                <Icons.Close />
              </div>

              {/* Khu vực trạng thái Account trên Mobile */}
              <div style={mobileAccountBox}>
                {currentUser ? (
                  <>
                    <p style={{ margin: 0, fontWeight: 500, color: "#1a1a1a", fontSize: 13.5, fontFamily: "'Montserrat', sans-serif" }}>✨ {currentUser.email || currentUser.phoneNumber}</p>
                    <p style={{ margin: "6px 0 0 0", fontSize: 13, color: "#e63946", fontWeight: 500, cursor: "pointer", fontFamily: "'Montserrat', sans-serif" }} onClick={handleLogout}>Đăng xuất tài khoản</p>
                  </>
                ) : (
                  <button style={mobileLoginBtn} onClick={() => go("/dang-nhap")}>
                    <Icons.Login /> Đăng nhập / Đăng ký
                  </button>
                )}
              </div>

              {/* Danh sách các Link điều hướng Mobile */}
              {[
                { name: "Trang chủ", path: "/" },
                { name: "Về chúng tôi", path: "/ve-chung-toi" },
                { name: "Thiết kế hoa", path: "/thiet-ke-bo-hoa" },
                { name: "Hoa đúng dịp", path: "/dung-dip" },
                { name: "Hoa hợp túi", path: "/hop-tui" },
                { name: "Mật ngữ loài hoa - Theo ý nghĩa", path: "/mat-ngu-loai-hoa/theo-y-nghia" },
                { name: "Mật ngữ loài hoa - Theo màu", path: "/mat-ngu-loai-hoa/theo-mau" },
                { name: "Liên hệ", path: "/lien-he" },
                { name: "Giỏ hàng của bạn", path: "/gio-hang-cua-ban" },
                { name: "Theo dõi đơn hàng", path: "/theo-doi-don-hang" }
              ].map((item, i) => (
                <motion.p key={i} style={menuItem} whileHover={{ x: 6, color: "#ff66a2" }} onClick={() => go(item.path)}>
                  {item.name}
                </motion.p>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* 🌸 KHU VỰC TRANG CON */}
      <div style={{ paddingTop: "70px", minHeight: "calc(100vh - 70px)" }}>
        <Outlet />
      </div>
    </div>
  );
}

/* --- HỆ THỐNG STYLE --- */
const header = { position: "fixed", top: 0, left: 0, right: 0, height: 90, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 32px", background: "rgba(255, 240, 246, 0.9)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.1)", zIndex: 1000 };
const logoContainer = { flex: "0 0 auto", display: "flex", alignItems: "center" };
const navCenterGroup = { display: "flex", gap: "24px", alignItems: "center", justifyContent: "center", flex: "1 1 auto", padding: "0 24px" };
const navRightGroup = { display: "flex", gap: "10px", alignItems: "center", justifyContent: "flex-end", flex: "0 0 auto" };
const logoStyle = { height: "96px", width: "auto", objectFit: "contain", cursor: "pointer" };
const actionIconBtn = { cursor: "pointer", width: "40px", height: "40px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "#000000", background: "transparent", border: "none", outline: "none", transition: "all 0.15s ease" };
const megaDropdown = { position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", background: "rgba(255, 240, 246, 0.9)", backdropFilter: "blur(10px)", borderRadius: "12px", boxShadow: "0 10px 30px rgba(0,0,0,0.08)", padding: "8px", minWidth: "190px", display: "flex", flexDirection: "column", gap: "2px", zIndex: 1500, border: "1px solid rgba(0,0,0,0.05)" };
const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.2)", backdropFilter: "blur(4px)", zIndex: 1001 };
const sideMenu = { position: "fixed", right: 0, top: 0, width: 300, maxWidth: "85vw", height: "100vh", background: "#ffffff", padding: "24px", zIndex: 1002, boxShadow: "-10px 0 40px rgba(0,0,0,0.03)", overflowY: "auto" };
const closeBtn = { display: "flex", justifyContent: "flex-end", cursor: "pointer", color: "#444", marginBottom: 20 };
const menuItem = { fontFamily: '"Montserrat", sans-serif', marginTop: 16, cursor: "pointer", fontSize: 14, fontWeight: 500, color: "#2c2c2c", transition: "all 0.2s ease" };
const dropdownMenu = { position: "absolute", top: "48px", right: "0px", background: "#fff", borderRadius: "14px", boxShadow: "0 12px 36px rgba(0,0,0,0.09)", padding: "8px", minWidth: "220px", display: "flex", flexDirection: "column", zIndex: 2000, border: "1px solid rgba(0,0,0,0.04)" };
const dropdownHeader = { padding: "8px 12px" };
const dropdownPhone = { fontFamily: '"Cormorant Garamond", serif', margin: 0, fontSize: "13px", fontWeight: "600", color: "#ff66a2", textTransform: "uppercase", letterSpacing: "1px" };
const dropdownEmail = { fontFamily: '"Montserrat", sans-serif', margin: "4px 0 0 0", fontSize: "12.5px", fontWeight: "500", color: "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const dropdownItem = { fontFamily: '"Montserrat", sans-serif', padding: "10px 12px", fontSize: "13px", color: "#333", fontWeight: 500, cursor: "pointer", borderRadius: "8px", transition: "all 0.2s ease", textAlign: "left", display: "flex", alignItems: "center" };
const mobileAccountBox = { background: "#fafafa", padding: "14px", borderRadius: "14px", marginBottom: "20px", display: "flex", flexDirection: "column", gap: "4px", border: "1px solid #f0f0f5" };
const mobileLoginBtn = { fontFamily: '"Montserrat", sans-serif', width: "100%", padding: "10px", background: "#ff66a2", color: "#fff", border: "none", borderRadius: "10px", fontWeight: "500", fontSize: "13px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" };